import { type FC } from 'react'
import { Box, Typography, Button, Card, CardMedia, CardContent, Stack } from '@mui/material'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import { useNavigate } from 'react-router-dom'

interface EducationalLevel {
  title: string
  description: string
  image: string
}

const educationalLevels: EducationalLevel[] = [
  {
    title: 'Preescolar',
    description: 'Formación inicial integral para niños y niñas',
    image: '/images/img3.jpg'
  },
  {
    title: 'Básica Primaria',
    description: 'Desarrollo de competencias fundamentales (1° a 5°)',
    image: '/images/img4.jpg'
  },
  {
    title: 'Básica Secundaria',
    description: 'Fortalecimiento académico y formación en valores (6° a 9°)',
    image: 'images/img8.jpg'
  },
  {
    title: 'Educación Media',
    description: 'Preparación para la vida universitaria y laboral (10° y 11°)',
    image: 'images/img11.jpg'
  }
]

const AboutUsEducative: FC = () => {
  const navigate = useNavigate()

  const handleContactClick = () => {
    navigate('/institutional')
    // Scroll to ContactUs section after navigation
    setTimeout(() => {
      const contactSection = document.getElementById('contact-us-section')
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }, 100)
  }

  return (
    <Box component="section" sx={{ pt: { xs: 8, sm: 10, md: 12 } }}>
      {/* Header Section */}
      <Box sx={{
        textAlign: 'center',
        mb: { xs: 4, sm: 5, md: 6 },
        maxWidth: { xs: '100%', sm: '600px', md: '800px' },
        mx: 'auto',
        px: { xs: 2, sm: 3, md: 4 }
      }}>
        <Typography
          variant="h3"
          component="h2"
          sx={{
            fontWeight: 700,
            mb: { xs: 1.5, sm: 2 },
            color: '#1a1a1a',
            fontSize: { xs: '1.5rem', sm: '1.875rem', md: '2rem', lg: '2.25rem' }
          }}
        >
          Oferta Educativa
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: '#555',
            lineHeight: { xs: 1.6, md: 1.8 },
            mb: { xs: 2, sm: 3 },
            fontSize: { xs: '0.938rem', sm: '1rem', md: '1.063rem' }
          }}
        >
          Ofrecemos una educación que prepara a nuestros estudiantes para afrontar los retos académicos,
          laborales y sociales con competencias sólidas y valores claros.
        </Typography>
        <Button
          variant="contained"
          onClick={handleContactClick}
          sx={{
            backgroundColor: 'primary.dark',
            color: '#fff',
            px: { xs: 2.5, sm: 3, md: 4 },
            py: { xs: 1, sm: 1.25 },
            textTransform: 'none',
            fontSize: { xs: '0.875rem', sm: '0.938rem', md: '0.95rem' },
            '&:hover': { backgroundColor: '#333' },
            mt: { xs: 2, sm: 3, md: 4 },
            width: { xs: '100%', sm: 'auto' },
            minWidth: { sm: 150 }
          }}
        >
          Contáctanos
        </Button>
      </Box>

      {/* Educational Levels Grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(auto-fit, minmax(220px, 1fr))'
          },
          gap: { xs: 2.5, sm: 3, md: 3.5 },
          mx: { xs: 0, sm: 4, md: 8, lg: 10 },
          my: { xs: 6, sm: 8, md: 10 },
          backgroundColor: '#ffffff',
          px: { xs: 2, sm: 3, md: 4 }
        }}
      >
        {educationalLevels.map((level) => (
          <Card
            key={level.title}
            sx={{
              boxShadow: { xs: '0 1px 4px rgba(0,0,0,0.1)', sm: '0 2px 8px rgba(0,0,0,0.1)' },
              overflow: 'hidden',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              '&:hover': {
                transform: { xs: 'scale(1.02)', sm: 'translateY(-4px)' },
                boxShadow: { sm: '0 4px 12px rgba(0,0,0,0.15)' }
              }
            }}
          >
            <CardMedia
              component="img"
              height="200"
              image={level.image}
              alt={level.title}
              sx={{
                objectFit: 'cover',
                height: { xs: 160, sm: 180, md: 200 }
              }}
            />
            <CardContent sx={{
              textAlign: 'center',
              p: { xs: 2, sm: 2.5, md: 3 }
            }}>
              <Typography
                variant="h6"
                component="h3"
                sx={{
                  fontWeight: 700,
                  mb: { xs: 0.75, sm: 1 },
                  color: '#1a1a1a',
                  fontSize: { xs: '1rem', sm: '1.063rem', md: '1.125rem' }
                }}
              >
                {level.title}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#666',
                  lineHeight: { xs: 1.5, md: 1.6 },
                  fontSize: { xs: '0.875rem', sm: '0.938rem' }
                }}
              >
                {level.description}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Location Section */}
      <Box sx={{
        textAlign: 'center',
        pt: { xs: 2, sm: 3 },
        borderTop: '1px solid #eee',
      }}>
        <Stack
          direction="row"
          spacing={{ xs: 0.75, sm: 1 }}
          sx={{
            justifyContent: 'center',
            alignItems: 'center',
            mb: { xs: 3, sm: 4 }
          }}
        >
          <LocationOnIcon sx={{
            color: 'primary.main',
            fontSize: { xs: '1.5rem', sm: '1.8rem', md: '2rem' }
          }} />
          <Typography
            variant="h5"
            component="h2"
            sx={{
              color: 'primary.main',
              fontWeight: 600,
              fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' }
            }}
          >
            ¿Dónde Estamos?
          </Typography>
        </Stack>

        {/* Map Container */}
        <Box
          component="iframe"
          src="https://www.google.com/maps/d/u/2/embed?mid=1tIXI5ToZVztzVvzCZX4EQ_TAS3MyvzM&ehbc=2E312F"
          sx={{
            width: '100%',
            height: { xs: 300, sm: 400, md: 500 },
            border: 0,
          }}
        />
      </Box>
    </Box>
  )
}

export default AboutUsEducative
